import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { queryKeys } from "@/shared/utils/queryKeys";
import { PlaidConnection, PlaidAccount, PlaidInstitution } from "@/shared/types/estateTypes";

interface CreateLinkTokenResponse {
  link_token: string;
  expiration: string;
}

interface ExchangeTokenResponse {
  connection_id: number;
  accounts_created: number;
  accounts: PlaidAccount[];
  institution_logo: string | null;
}

interface RefreshBalancesResponse {
  refreshed_count: number;
  accounts: Array<{
    id: number;
    name: string;
    balance: number;
    previous_balance: number;
  }>;
}

interface ConnectionsResponse {
  connections: PlaidConnection[];
}

interface SearchInstitutionsResponse {
  institutions: PlaidInstitution[];
}

// API functions
async function searchInstitutions(
  estateId: number,
  query: string,
  products?: string[],
): Promise<SearchInstitutionsResponse> {
  const { data, error } = await supabase.functions.invoke(`financials/${estateId}/banking/search`, {
    body: { query, products },
    method: "POST",
  });

  if (error) {
    throw new Error(error.message || "Failed to search institutions");
  }

  return data;
}

async function createLinkToken(
  estateId: number,
  institutionId?: string,
): Promise<CreateLinkTokenResponse> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/link-token`,
    {
      body: { estate_id: estateId, institution_id: institutionId },
      method: "POST",
    },
  );

  if (error) {
    throw new Error(error.message || "Failed to create link token");
  }

  return data;
}

async function exchangeToken(
  estateId: number,
  publicToken: string,
  institution: { institution_id: string; name: string },
  purpose?: "estate_account" | "discovery",
  accountIds?: string[],
): Promise<ExchangeTokenResponse> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/exchange`,
    {
      body: {
        estate_id: estateId,
        public_token: publicToken,
        institution,
        purpose,
        ...(accountIds?.length ? { account_ids: accountIds } : {}),
      },
      method: "POST",
    },
  );

  if (error) {
    throw new Error(error.message || "Failed to exchange token");
  }

  return data;
}

async function refreshBalances(
  estateId: number,
  connectionId: number,
): Promise<RefreshBalancesResponse> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/${connectionId}/refresh`,
    {
      method: "POST",
    },
  );

  if (error) {
    throw new Error(error.message || "Failed to refresh balances");
  }

  return data;
}

async function fetchConnections(
  estateId: number,
  purpose?: string,
): Promise<ConnectionsResponse> {
  const path = purpose
    ? `financials/${estateId}/banking/connections?purpose=${purpose}`
    : `financials/${estateId}/banking/connections`;
  const { data, error } = await supabase.functions.invoke(path, {
    method: "GET",
  });

  if (error) {
    throw new Error(error.message || "Failed to fetch connections");
  }

  return data;
}

async function disconnectConnection(
  estateId: number,
  connectionId: number,
): Promise<{ success: boolean; accounts_unlinked: number }> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/${connectionId}`,
    {
      method: "DELETE",
    },
  );

  if (error) {
    throw new Error(error.message || "Failed to disconnect");
  }

  return data;
}

export function usePlaid(
  estateId: number | null | undefined,
  purpose?: "estate_account" | "discovery",
) {
  const queryClient = useQueryClient();

  // Query: Get existing connections
  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
    error: connectionsError,
  } = useQuery({
    queryKey: [...queryKeys.plaid.connections(estateId!), purpose],
    queryFn: () => fetchConnections(estateId!, purpose),
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  // Mutation: Search institutions
  const searchInstitutionsMutation = useMutation({
    mutationFn: ({ query, products }: { query: string; products?: string[] }) =>
      searchInstitutions(estateId!, query, products),
  });

  // Mutation: Create link token
  const createLinkTokenMutation = useMutation({
    mutationFn: (institutionId?: string) => createLinkToken(estateId!, institutionId),
  });

  // Mutation: Exchange token and create accounts
  const exchangeTokenMutation = useMutation({
    mutationFn: ({
      publicToken,
      institution,
      accountIds,
    }: {
      publicToken: string;
      institution: { institution_id: string; name: string };
      accountIds?: string[];
    }) => exchangeToken(estateId!, publicToken, institution, purpose, accountIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plaid.connections(estateId!) });
    },
  });

  // Mutation: Refresh balances
  const refreshBalancesMutation = useMutation({
    mutationFn: (connectionId: number) => refreshBalances(estateId!, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plaid.connections(estateId!) });
    },
  });

  // Mutation: Disconnect
  const disconnectMutation = useMutation({
    mutationFn: (connectionId: number) => disconnectConnection(estateId!, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plaid.connections(estateId!) });
    },
  });

  return {
    connections: connectionsData?.connections || [],
    isLoadingConnections,
    connectionsError,
    searchInstitutions: searchInstitutionsMutation.mutateAsync,
    isSearching: searchInstitutionsMutation.isPending,
    createLinkToken: createLinkTokenMutation.mutateAsync,
    isCreatingLinkToken: createLinkTokenMutation.isPending,
    exchangeToken: exchangeTokenMutation.mutateAsync,
    isExchanging: exchangeTokenMutation.isPending,
    refreshBalances: refreshBalancesMutation.mutateAsync,
    isRefreshing: refreshBalancesMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
  };
}
